import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, Loader2, Calendar, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No invitation token provided');
      return;
    }
    
    acceptInvitation();
  }, [token]);

  const acceptInvitation = async () => {
    try {
      const response = await fetch(`${API}/api/organizers/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invitation');
      }
      
      setInvitation(data);
      setStatus('success');
      toast.success('Invitation accepted!');
      
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  const handleGoToDashboard = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center" data-testid="invitation-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-white text-lg">Accepting invitation...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4" data-testid="invitation-error">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-gray-800 rounded-2xl p-8 text-center border border-gray-700"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Invitation Error</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <div className="flex flex-col gap-3">
            <Link to="/login">
              <Button className="w-full bg-green-600 hover:bg-green-700">
                Go to Login
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-gray-700">
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4" data-testid="invitation-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-gray-800 rounded-2xl p-8 text-center border border-gray-700"
      >
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Invitation Accepted!</h1>
        
        {invitation?.event_name && (
          <div className="my-6 p-4 bg-gray-700/50 rounded-xl">
            <div className="flex items-center justify-center gap-2 text-gray-400 mb-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">You've been added to</span>
            </div>
            <p className="text-xl font-semibold text-white">{invitation.event_name}</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <Shield className="w-4 h-4 text-green-500" />
              <span className="text-green-500 text-sm capitalize">{invitation.role?.replace('_', ' ')}</span>
            </div>
          </div>
        )}
        
        <p className="text-gray-400 mb-8">
          Sign in with Google to access your event dashboard and start managing.
        </p>
        
        <Button
          onClick={handleGoToDashboard}
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-medium"
          data-testid="go-to-dashboard-btn"
        >
          Continue to Dashboard
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        <p className="mt-6 text-sm text-gray-500">
          Make sure to sign in with the email address the invitation was sent to.
        </p>
      </motion.div>
    </div>
  );
};

export default AcceptInvitationPage;
